import { useEffect, useCallback, Fragment } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { initialize as formInitialize, reduxForm } from 'redux-form';
import { styled } from '@mui/system';

// Libs
import { GridContainer, GridItem, Input } from '@formComponents';
import { Button } from '@libComponents';
import ContentContainer from '@libComponents/ContentContainer';
import { hasFormErrorSelector } from 'common/redux.selectors';
import AutocompleteFetch from '@formComponents/AutocompleteFetch';
import DatePickerBase from '@formComponents/DatePicker';
import { Autocomplete, CircularProgress, useTheme } from '@mui/material';
import Label from '@baseComponents/Label';
import InputBase from '@mui/material/InputBase';
import { DOMAIN_NAME } from 'common/common';
import {
    BRANCH,
    DEPARTMENT,
    DESIGNATION,
    EMPLOYEE_CATEGORY,
    CALENDER,
    GET_ALL_EMPLOYEE_NAME_EMAIL
} from 'common/redux.datasets';
import dayjs from 'dayjs';
import { DOMAIN_NAME as COMMON_DOMAIN } from 'common/common';
import { required } from '@libs/validationRules';
import { getAllEmployeeEnhancedSelector } from 'common/redux.selectors';
import { fetchDatasets, datasetLoadingSelector } from '@libs/datasets';

// Import components with gear icons for edit/delete functionality
import Department from 'common/components/department';
import Branch from 'common/components/branch';
import Designation from 'common/components/designation';
import EmployeeCategory from 'common/components/employeeCategory';
import Calender from 'common/components/calender';

import { FORM_NAME } from './common';
import {
    getEmployeeInfo,
    updateEmployeeInfo
} from 'employee/ManageEmployee/redux.actions';

const propTypes = {
    mode: PropTypes.oneOf(['update'])
};
const defaultProps = {
    mode: 'update'
};

const ButtonWrapper = styled('div')({
    position: 'absolute',
    bottom: 0,
    right: '50%',
    transform: 'translate(50%, 50%)',
    zIndex: 4
});

const StyledButton = styled(Button)(({ theme: { spacing, typography } }) => ({
    height: spacing(4.75),
    width: spacing(25),
    fontSize: typography.subtitle1.fontSize,
    marginLeft: spacing(0.75),
    marginRight: spacing(0.75)
}));

const StyledInput = styled(InputBase)(
    ({ theme: { spacing, palette, typography, shape } }) => ({
        width: '100%',
        height: '35px',
        border: '1px solid',
        borderColor: palette.border.main,
        borderRadius: shape.borderRadius,
        paddingLeft: spacing(2),
        fontSize: typography.subtitle2.fontSize,
        '&:hover': {
            borderColor: palette.border.dark
        },
        '&.Mui-focused': {
            borderColor: palette.grey[500],
            '&:hover': {
                borderColor: palette.grey[700]
            }
        },
        '&.Mui-disabled': {
            backgroundColor: palette.background.disabled,
            color: palette.secondary.main
        }
    })
);

const StyledAutocompleteWrapper = styled('div')(({ theme: { spacing } }) => ({
    width: '100%'
}));

const ManageEmployeeInfo = reduxForm({
    form: FORM_NAME,
    destroyOnUnmount: false,
    onSubmit: (v) => v
})(({ mode, handleSubmit: handleReduxFormSubmit, ...rest }) => {
    const { state } = useLocation();
    const theme = useTheme();
    const RXDispatch = useDispatch();

    const { email } = state || {};
    const hasError = useSelector((state) =>
        hasFormErrorSelector(state, FORM_NAME)
    );

    // Get all employees for reporting manager dropdown
    const allEmployees = useSelector((state) =>
        getAllEmployeeEnhancedSelector(
            state,
            COMMON_DOMAIN,
            GET_ALL_EMPLOYEE_NAME_EMAIL
        )
    );
    const loadingEmployees = useSelector((state) =>
        datasetLoadingSelector(
            state,
            COMMON_DOMAIN,
            GET_ALL_EMPLOYEE_NAME_EMAIL
        )
    );

    // Get current form values for reporting manager
    const formValues = useSelector(
        (state) => state.form[FORM_NAME]?.values || {}
    );
    const selectedReportingManager =
        allEmployees.find((emp) => emp.email === formValues.reportingManager) ||
        null;

    const handleGetEmployeeInfo = useCallback(
        (employeeData) => {
            RXDispatch(formInitialize(FORM_NAME, employeeData));
        },
        [RXDispatch]
    );

    const handleUpdateSuccess = useCallback(
        (payload) => {
            // After successful update, refetch the employee data to refresh the form
            if (email && !payload?.error) {
                RXDispatch(
                    getEmployeeInfo({
                        handleSubmit: handleGetEmployeeInfo,
                        payload: { employeeEmail: email }
                    })
                );
            }
        },
        [email, RXDispatch, handleGetEmployeeInfo]
    );

    const handleUpdate = useCallback(() => {
        if (hasError) {
            handleReduxFormSubmit();
            return;
        }
        RXDispatch(updateEmployeeInfo({ handleSubmit: handleUpdateSuccess }));
    }, [hasError, handleReduxFormSubmit, RXDispatch, handleUpdateSuccess]);

    useEffect(() => {
        if (email) {
            // Fetch employees for reporting manager dropdown
            RXDispatch(fetchDatasets(GET_ALL_EMPLOYEE_NAME_EMAIL));
            // Fetch and populate employee data
            RXDispatch(
                getEmployeeInfo({
                    handleSubmit: handleGetEmployeeInfo,
                    payload: { employeeEmail: email }
                })
            );
        }
    }, [email, RXDispatch, handleGetEmployeeInfo]);

    return (
        <div style={{ padding: '16px' }}>
            <ContentContainer
                title='Employee Email'
                AdornmentComponent={<Fragment />}
                sx={{ display: 'none' }}
            >
                <GridContainer>
                    <GridItem md={4}>
                        <Input
                            name='employeeEmail'
                            placeholder='Employee Email'
                            label='Employee E-Mail'
                            disabled={true}
                            autoComplete='do-not-autofill'
                        />
                    </GridItem>
                </GridContainer>
            </ContentContainer>

            <ContentContainer
                title='Organizational Information'
                AdornmentComponent={<Fragment />}
            >
                <GridContainer>
                    <GridItem md={3}>
                        <Input
                            name='employeeId'
                            placeholder='Employee ID'
                            label='Employee ID'
                            required={true}
                            validate={required}
                        />
                    </GridItem>
                    <GridItem
                        md={3}
                        sx={{ display: 'flex', alignItems: 'center' }}
                    >
                        <StyledAutocompleteWrapper>
                            <Label
                                label='Reporting Manager'
                                htmlFor='reportingManager'
                            />
                            <Autocomplete
                                disablePortal
                                options={allEmployees}
                                getOptionLabel={(option) => {
                                    const name = `${option.firstname || ''} ${
                                        option.middlename || ''
                                    } ${option.lastname || ''}`.trim();
                                    return name || option.email || '';
                                }}
                                value={selectedReportingManager}
                                onChange={(event, newValue) => {
                                    RXDispatch({
                                        type: '@@redux-form/CHANGE',
                                        meta: {
                                            form: FORM_NAME,
                                            field: 'reportingManager',
                                            touch: false,
                                            persistentSubmitErrors: false
                                        },
                                        payload: newValue ? newValue.email : ''
                                    });
                                }}
                                loading={loadingEmployees}
                                renderInput={(params) => (
                                    <StyledInput
                                        ref={params.InputProps.ref}
                                        inputProps={params.inputProps}
                                        placeholder='Search by name or email'
                                        endAdornment={
                                            <>
                                                {loadingEmployees && (
                                                    <CircularProgress
                                                        color='inherit'
                                                        size={16}
                                                        sx={{ mr: 1 }}
                                                    />
                                                )}
                                                {params.InputProps.endAdornment}
                                            </>
                                        }
                                    />
                                )}
                                renderOption={(props, option) => {
                                    return (
                                    <li
                                        {...props}
                                        style={{
                                            padding: '10px 14px',
                                            borderBottom: `1px solid ${theme.palette.divider}`
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                width: '100%'
                                            }}
                                        >
                                            <div
                                                style={{
                                                    fontWeight: '500',
                                                    fontSize: '14px',
                                                    color: theme.palette.text.primary
                                                }}
                                            >
                                                {`${option.firstname || ''} ${
                                                    option.middlename || ''
                                                } ${
                                                    option.lastname || ''
                                                }`.trim()}
                                            </div>
                                            <div
                                                style={{
                                                    fontSize: '12px',
                                                    color: theme.palette.text.primary
                                                }}
                                            >
                                                <p>{option.email}</p>
                                                {option.designation && (
                                                    <p>{option.designation}</p>
                                                )}
                                            </div>
                                        </div>
                                    </li>
                                    );
                                }}
                            />
                        </StyledAutocompleteWrapper>
                    </GridItem>
                    <GridItem md={3}>
                        <DatePickerBase
                            name='joiningDate'
                            placeholder='Joining Date'
                            label='Joining Date'
                            minDate='2000-01-01'
                            maxDate={dayjs().add(1, 'year')}
                            required={true}
                            validate={required}
                        />
                    </GridItem>
                </GridContainer>
            </ContentContainer>

            <ContentContainer
                title='Department & Branch Information'
                AdornmentComponent={<Fragment />}
            >
                <GridContainer>
                    <GridItem md={3}>
                        <AutocompleteFetch
                            name='department'
                            placeholder='Department'
                            label='Department'
                            domain={DOMAIN_NAME}
                            entity={DEPARTMENT}
                            required={true}
                            validate={required}
                            ContainerComponent={Department}
                        />
                    </GridItem>
                    <GridItem md={3}>
                        <AutocompleteFetch
                            name='officeBranch'
                            placeholder='Branch'
                            label='Branch'
                            required={true}
                            validate={required}
                            entity={BRANCH}
                            domain={DOMAIN_NAME}
                            ContainerComponent={Branch}
                        />
                    </GridItem>
                    <GridItem md={3}>
                        <AutocompleteFetch
                            name='designation'
                            placeholder='Designation'
                            label='Designation'
                            required={true}
                            validate={required}
                            entity={DESIGNATION}
                            domain={DOMAIN_NAME}
                            ContainerComponent={Designation}
                        />
                    </GridItem>
                    <GridItem md={3}>
                        <AutocompleteFetch
                            name='employeeCategory'
                            placeholder='Employee Category'
                            label='Employee Category'
                            required={true}
                            validate={required}
                            domain={DOMAIN_NAME}
                            entity={EMPLOYEE_CATEGORY}
                            ContainerComponent={EmployeeCategory}
                        />
                    </GridItem>
                </GridContainer>
            </ContentContainer>

            <ContentContainer
                title='Calendar Configuration'
                AdornmentComponent={<Fragment />}
            >
                <GridContainer>
                    <GridItem md={3}>
                        <AutocompleteFetch
                            name='calenderId'
                            placeholder='Select Calendar'
                            label='Select Calendar'
                            required={true}
                            validate={required}
                            domain={DOMAIN_NAME}
                            entity={CALENDER}
                            ContainerComponent={Calender}
                        />
                    </GridItem>
                </GridContainer>
                <ButtonWrapper>
                    <StyledButton onClick={handleUpdate}>UPDATE</StyledButton>
                </ButtonWrapper>
            </ContentContainer>
        </div>
    );
});

ManageEmployeeInfo.propTypes = propTypes;
ManageEmployeeInfo.defaultProps = defaultProps;

export { ManageEmployeeInfo as default, ManageEmployeeInfo };
